import { getCurrentDisplayTz } from "./settings";

const currencyFmt = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

export function formatCurrency(value: number | null | undefined): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return currencyFmt.format(n);
}

export function formatSignedCurrency(value: number | null | undefined): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  if (n === 0) return formatCurrency(0);
  return (n > 0 ? "+" : "−") + formatCurrency(Math.abs(n));
}

/** ISO string → localized string in the user's chosen display timezone. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: getCurrentDisplayTz(),
  });
}

/**
 * For <input type="datetime-local"> — this input expects "YYYY-MM-DDTHH:MM"
 * with NO timezone marker. We format the ISO into the user's tz so what they
 * see and pick is in their local time, but we know the wall-clock components
 * we need.
 */
export function toLocalDatetimeInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const tz = getCurrentDisplayTz();
  // en-CA gives YYYY-MM-DD; HH:mm via en-GB with hourCycle h23.
  const dateStr = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
  // Seconds are included deliberately. Without them the datetime-local
  // round-trip silently rewrites happened_at to :00, which used to break the
  // MT sync's dedupe key and re-import the entry as a duplicate.
  const timeStr = new Intl.DateTimeFormat("en-GB", {
    timeZone: tz,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).format(d);
  return `${dateStr}T${timeStr}`;
}

/**
 * Inverse: given "YYYY-MM-DDTHH:MM[:SS]" from a datetime-local input, treat those
 * wall-clock components as belonging to the display timezone and return an
 * ISO instant. Uses Intl to compute the tz offset at that moment.
 */
export function fromLocalDatetimeInput(local: string): string {
  if (!local) return "";
  const tz = getCurrentDisplayTz();
  // Interpret as UTC first, then correct by the offset the tz had at that moment.
  const asUtc = new Date(local + "Z");
  if (Number.isNaN(asUtc.getTime())) return "";
  const offsetMs = tzOffsetMs(tz, asUtc);
  return new Date(asUtc.getTime() - offsetMs).toISOString();
}

function tzOffsetMs(tz: string, at: Date): number {
  // How many ms the local wall-clock is ahead of UTC at this instant.
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });
  const parts = dtf.formatToParts(at);
  const map: Record<string, string> = {};
  for (const p of parts) map[p.type] = p.value;
  const asLocal = Date.UTC(
    Number(map.year),
    Number(map.month) - 1,
    Number(map.day),
    Number(map.hour),
    Number(map.minute),
    Number(map.second),
  );
  return asLocal - at.getTime();
}

/**
 * "2 minutes ago" in the given locale, via Intl.RelativeTimeFormat — so the
 * phrasing is translated by the platform rather than needing a string per
 * language per unit. Falls back to an absolute date for anything older than a
 * week, where "3 weeks ago" is less useful than the date itself.
 */
export function formatRelativeTime(
  iso: string | null | undefined,
  locale: string,
): string {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";

  const seconds = Math.round((then - Date.now()) / 1000);
  const abs = Math.abs(seconds);
  if (abs > 7 * 86400) return formatDate(iso);

  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ["second", 60],
    ["minute", 3600],
    ["hour", 86400],
    ["day", Infinity],
  ];
  const divisors: Record<string, number> = { second: 1, minute: 60, hour: 3600, day: 86400 };

  let unit: Intl.RelativeTimeFormatUnit = "day";
  for (const [u, limit] of units) {
    if (abs < limit) { unit = u; break; }
  }

  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
    return rtf.format(Math.round(seconds / divisors[unit]), unit);
  } catch {
    return formatDate(iso);
  }
}
